import { useContext, useState } from 'react'

import { SingleValue } from 'react-select'

import { ErrorContext } from '@cash-web/shared-data-access-error'

import { AddressInputPrefill } from './AddressInput'
import { useGooglePlaces } from './useGooglePlaces'

export type Option = { prediction: { value: string }; label: string; placeId: string }

const GOOGLE_PLACES_API_KEY_PRODUCTION = 'AIzaSyBpglC79byCzlOKCpb3eZ8JVXGb9qt9RSE'
const GOOGLE_PLACES_API_KEY_STAGING = 'AIzaSyCnYH-B8SLr9ngrN2iCaLPdq-zvK4pHrTQ'

const apiKey =
  process.env['NODE_ENV'] === 'production' ? GOOGLE_PLACES_API_KEY_PRODUCTION : GOOGLE_PLACES_API_KEY_STAGING

const googlePlacesToPrefill = (place: google.maps.places.PlaceResult): AddressInputPrefill => ({
  street: place.name ?? '',
  city: place.address_components?.find(({ types }) => types.includes('locality'))?.long_name ?? '',
  state: place.address_components?.find(({ types }) => types.includes('administrative_area_level_1'))?.short_name ?? '',
  postalCode: place.address_components?.find(({ types }) => types.includes('postal_code'))?.long_name ?? '',
})

type UseAutocompleteAddressInputArgs = { isInitialInput: boolean }
export const useAutocompleteAddressInput = ({ isInitialInput }: UseAutocompleteAddressInputArgs) => {
  const [initialInput, setInitialInput] = useState<boolean>(isInitialInput)
  const { autocompleteService, placesService, sessionToken } = useGooglePlaces({ apiKey })
  const [placesAddress, setPlacesAddress] = useState<AddressInputPrefill | undefined>(undefined)
  const [isLoadingAddress, setIsLoadingAddress] = useState<boolean>(false)
  const { clearError } = useContext(ErrorContext)

  const chooseAddress = async (option: SingleValue<Option>) => {
    const placeId = option?.placeId
    if (!placeId) return
    setIsLoadingAddress(true)
    placesService?.getDetails({ placeId }, result => {
      setIsLoadingAddress(false)
      if (!result) return
      setInitialInput(false)
      setPlacesAddress(googlePlacesToPrefill(result))
      clearError()
    })
  }

  const getPredictions = async (value: string) => {
    const results = await autocompleteService?.getPlacePredictions({
      input: value,
      sessionToken,
      componentRestrictions: { country: 'us' },
    })
    return (
      results?.predictions.map(predictions => ({
        prediction: { value: predictions.description },
        label: predictions.description,
        placeId: predictions.place_id,
      })) ?? []
    )
  }

  return { isInitialInput: initialInput, getPredictions, chooseAddress, placesAddress, isLoadingAddress }
}
